import React, { useState } from 'react';
import Card from '../../components/ui/Card';
import { CalendarIcon, DownloadIcon, ChevronLeftIcon, ChevronRightIcon, FileTextIcon, PlayCircleIcon } from 'lucide-react';
// Mock data for aggregators
const mockAggregators = [{
  id: 'agg1',
  name: 'Global Payments Network'
}, {
  id: 'agg2',
  name: 'Metro Financial Services'
}, {
  id: 'agg3',
  name: 'Digital Commerce Hub'
}, {
  id: 'agg4',
  name: 'PayTech Solutions'
}];
// Mock data for merchants
const mockMerchants = [{
  id: 'mer1',
  name: 'Global Electronics',
  aggregatorId: 'agg1'
}, {
  id: 'mer2',
  name: 'Fashion Boutique',
  aggregatorId: 'agg1'
}, {
  id: 'mer3',
  name: 'Tech Store',
  aggregatorId: 'agg2'
}, {
  id: 'mer4',
  name: 'Food Court',
  aggregatorId: 'agg2'
}, {
  id: 'mer5',
  name: 'Book Shop',
  aggregatorId: 'agg3'
}, {
  id: 'mer6',
  name: 'Sports Gear',
  aggregatorId: 'agg3'
}];
// Generate mock transaction details
const generateTransactionDetails = () => {
  const transactions = [];
  const statuses = ['Success', 'Failed', 'Pending'];
  const merchants = mockMerchants;
  // Generate 50 transactions
  for (let i = 1; i <= 50; i++) {
    const merchant = merchants[Math.floor(Math.random() * merchants.length)];
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const amount = (Math.random() * 5000 + 100).toFixed(2);
    // Generate dates within the last 30 days
    const date = new Date();
    date.setDate(date.getDate() - Math.floor(Math.random() * 30));
    transactions.push({
      id: `TXN${String(i).padStart(6, '0')}`,
      reference: `REF${String(i).padStart(8, '0')}`,
      merchantName: merchant.name,
      merchantId: merchant.id,
      aggregatorId: merchant.aggregatorId,
      amount: parseFloat(amount),
      status,
      date: date.toISOString(),
      customerName: `Customer ${i}`,
      paymentMethod: Math.random() > 0.5 ? 'QR Code' : 'Card Payment'
    });
  }
  // Sort by date descending
  return transactions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
};
const TransactionDetailReport: React.FC = () => {
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30); // Set to 30 days ago
    return date.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [selectedAggregator, setSelectedAggregator] = useState('');
  const [selectedMerchant, setSelectedMerchant] = useState('');
  const [transactionReference, setTransactionReference] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [transactionDetails, setTransactionDetails] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [reportGenerated, setReportGenerated] = useState(false);
  const itemsPerPage = 10;
  const filteredMerchants = selectedAggregator ? mockMerchants.filter(m => m.aggregatorId === selectedAggregator) : mockMerchants;
  const generateReport = () => {
    // Generate fresh transaction data
    let filtered = generateTransactionDetails();
    // Apply date filter
    filtered = filtered.filter(t => {
      const transactionDate = new Date(t.date);
      const from = new Date(fromDate);
      const to = new Date(toDate);
      to.setHours(23, 59, 59, 999);
      return transactionDate >= from && transactionDate <= to;
    });
    // Apply aggregator filter
    if (selectedAggregator) {
      filtered = filtered.filter(t => t.aggregatorId === selectedAggregator);
    }
    // Apply merchant filter
    if (selectedMerchant) {
      filtered = filtered.filter(t => t.merchantId === selectedMerchant);
    }
    // Apply transaction reference filter
    if (transactionReference) {
      filtered = filtered.filter(t => t.reference.toLowerCase().includes(transactionReference.toLowerCase()));
    }
    // Apply status filter
    if (statusFilter !== 'All') {
      filtered = filtered.filter(t => t.status === statusFilter);
    }
    setTransactionDetails(filtered);
    setCurrentPage(1);
    setReportGenerated(true);
  };
  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return transactionDetails.slice(startIndex, endIndex);
  };
  const getTotalPages = () => {
    return Math.ceil(transactionDetails.length / itemsPerPage);
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 2
    }).format(amount);
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  const resetFilters = () => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    setFromDate(date.toISOString().split('T')[0]);
    setToDate(new Date().toISOString().split('T')[0]);
    setSelectedAggregator('');
    setSelectedMerchant('');
    setTransactionReference('');
    setStatusFilter('All');
    setTransactionDetails([]);
    setReportGenerated(false);
  };
  return <div className="space-y-6" data-id="element-6484">
      <div data-id="element-6485">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-6486">
          Transaction Detail Report
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-6487">
          View detailed information about individual transactions
        </p>
      </div>
      {/* Filters */}
      <Card data-id="element-6488">
        <div className="flex items-center justify-between mb-4" data-id="element-6489">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-6490">
            Filters
          </h3>
          <button onClick={resetFilters} className="text-sm text-blue-600 dark:text-blue-400 hover:underline" data-id="element-6491">
            Reset Filters
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-6492">
          <div data-id="element-6493">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6494">
              From Date
            </label>
            <div className="relative" data-id="element-6495">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-6496">
                <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-6497" />
              </div>
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800" data-id="element-6498" />
            </div>
          </div>
          <div data-id="element-6499">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6500">
              To Date
            </label>
            <div className="relative" data-id="element-6501">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-6502">
                <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-6503" />
              </div>
              <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800" data-id="element-6504" />
            </div>
          </div>
          <div data-id="element-6505">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6506">
              Aggregator
            </label>
            <select value={selectedAggregator} onChange={e => {
            setSelectedAggregator(e.target.value);
            setSelectedMerchant('');
          }} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800" data-id="element-6507">
              <option value="" data-id="element-6508">All Aggregators</option>
              {mockAggregators.map(agg => <option key={agg.id} value={agg.id} data-id="element-6509">
                  {agg.name}
                </option>)}
            </select>
          </div>
          <div data-id="element-6510">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6511">
              Merchant
            </label>
            <select value={selectedMerchant} onChange={e => setSelectedMerchant(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800" data-id="element-6512">
              <option value="" data-id="element-6513">All Merchants</option>
              {filteredMerchants.map(merchant => <option key={merchant.id} value={merchant.id} data-id="element-6514">
                  {merchant.name}
                </option>)}
            </select>
          </div>
          <div data-id="element-6515">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6516">
              Transaction Reference
            </label>
            <input type="text" value={transactionReference} onChange={e => setTransactionReference(e.target.value)} placeholder="Enter reference number" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800" data-id="element-6517" />
          </div>
          <div data-id="element-6518">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6519">
              Status
            </label>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800" data-id="element-6520">
              <option value="All" data-id="element-6521">All</option>
              <option value="Success" data-id="element-6522">Success</option>
              <option value="Failed" data-id="element-6523">Failed</option>
              <option value="Pending" data-id="element-6524">Pending</option>
            </select>
          </div>
        </div>
        <div className="mt-6 flex justify-end" data-id="element-6525">
          <button onClick={generateReport} className="inline-flex items-center px-6 py-2.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-6526">
            <PlayCircleIcon className="h-5 w-5 mr-2" data-id="element-6527" />
            Generate Report
          </button>
        </div>
      </Card>
      {/* Report Content */}
      <Card data-id="element-6528">
        <div className="flex items-center justify-between mb-4" data-id="element-6529">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-6530">
            Transaction Details
          </h3>
          {reportGenerated && transactionDetails.length > 0 && <button onClick={() => console.log('Export')} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-6531">
              <DownloadIcon className="h-4 w-4 mr-2" data-id="element-6532" />
              Export
            </button>}
        </div>
        {!reportGenerated ? <div className="text-center py-12" data-id="element-6533">
            <FileTextIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-6534" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white" data-id="element-6535">
              No Report Generated
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-6536">
              Select your filters above and click "Generate Report" to view
              transaction details.
            </p>
          </div> : transactionDetails.length === 0 ? <div className="text-center py-12" data-id="element-6537">
            <FileTextIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-6538" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white" data-id="element-6539">
              No Transactions Found
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-6540">
              No transactions match your selected filters. Try adjusting your
              criteria.
            </p>
          </div> : <>
            <div className="mb-4 text-sm text-gray-500 dark:text-gray-400" data-id="element-6541">
              Showing {transactionDetails.length} transaction(s)
            </div>
            <div className="overflow-x-auto" data-id="element-6542">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-6543">
                <thead className="bg-gray-50 dark:bg-gray-700" data-id="element-6544">
                  <tr data-id="element-6545">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase" data-id="element-6546">
                      Transaction ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase" data-id="element-6547">
                      Reference
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase" data-id="element-6548">
                      Merchant
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase" data-id="element-6549">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase" data-id="element-6550">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase" data-id="element-6551">
                      Payment Method
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase" data-id="element-6552">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase" data-id="element-6553">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-6554">
                  {getCurrentPageData().map((transaction: any) => <tr key={transaction.id} data-id="element-6555">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" data-id="element-6556">
                        {transaction.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-6557">
                        {transaction.reference}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-6558">
                        {transaction.merchantName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-6559">
                        {transaction.customerName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-6560">
                        {formatCurrency(transaction.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-6561">
                        {transaction.paymentMethod}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-6562">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${transaction.status === 'Success' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : transaction.status === 'Failed' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'}`} data-id="element-6563">
                          {transaction.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-6564">
                        {formatDate(transaction.date)}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {getTotalPages() > 1 && <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 px-4 py-3 sm:px-6 mt-4" data-id="element-6565">
                <div className="flex flex-1 justify-between sm:hidden" data-id="element-6566">
                  <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50" data-id="element-6567">
                    Previous
                  </button>
                  <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, getTotalPages()))} disabled={currentPage === getTotalPages()} className="relative ml-3 inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50" data-id="element-6568">
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between" data-id="element-6569">
                  <div data-id="element-6570">
                    <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-6571">
                      Showing{' '}
                      <span className="font-medium" data-id="element-6572">
                        {(currentPage - 1) * itemsPerPage + 1}
                      </span>{' '}
                      to{' '}
                      <span className="font-medium" data-id="element-6573">
                        {Math.min(currentPage * itemsPerPage, transactionDetails.length)}
                      </span>{' '}
                      of{' '}
                      <span className="font-medium" data-id="element-6574">
                        {transactionDetails.length}
                      </span>{' '}
                      results
                    </p>
                  </div>
                  <div data-id="element-6575">
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" data-id="element-6576">
                      <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50" data-id="element-6577">
                        <ChevronLeftIcon className="h-5 w-5" data-id="element-6578" />
                      </button>
                      {Array.from({
                  length: getTotalPages()
                }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage(page)} className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === page ? 'z-10 bg-blue-50 dark:bg-blue-900/30 border-blue-500 text-blue-600 dark:text-blue-400' : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`} data-id="element-6579">
                          {page}
                        </button>)}
                      <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, getTotalPages()))} disabled={currentPage === getTotalPages()} className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50" data-id="element-6580">
                        <ChevronRightIcon className="h-5 w-5" data-id="element-6581" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>}
          </>}
      </Card>
    </div>;
};
export default TransactionDetailReport;